import { useEffect, useRef, useState } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { debug } from 'debug';
import { env } from '~/env/client';
import { useLatestValueRef } from '~/lib/use-latest-value-ref';

const logger = debug('app:event-source');

const RECONNECT_DELAY = 3000;

interface EventSourceHookOptions<TData> {
  parseMessage: (data: string) => TData | null;
}

interface EventSourceHookParams<TData> {
  pathname: string;
  enabled?: boolean;
  onMessage?: (data: TData) => void;
  getAccessToken?: () => string | null;
}

interface EventSourceHookReturn {
  isConnected: boolean;
  error: string | null;
  disconnect: () => void;
}

export const createEventSourceHook = <TData>(options: EventSourceHookOptions<TData>) => {
  const { parseMessage } = options;

  return ({
    pathname,
    onMessage,
    enabled = true,
    getAccessToken,
  }: EventSourceHookParams<TData>): EventSourceHookReturn => {
    const latestRef = useLatestValueRef({ onMessage, getAccessToken });

    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      const url = new URL(env.NEXT_PUBLIC_SWAGGER_URL);
      url.pathname = pathname;

      if (!enabled) {
        return;
      }

      logger('Starting listening to:', url.pathname);

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const connect = async () => {
        try {
          const token = latestRef.current.getAccessToken?.();
          const headers: Record<string, string> = {};
          if (token) {
            headers.Authorization = `Bearer ${token}`;
          }

          logger('Connect');
          await fetchEventSource(url.toString(), {
            signal: abortController.signal,
            headers,
            openWhenHidden: true,
            onopen: async (response) => {
              if (response.ok) {
                setIsConnected(true);
                setError(null);
                logger('Connection opened');
                return Promise.resolve();
              } else {
                const errorMessage = `Failed to connect: ${response.status} ${response.statusText}`;
                setError(errorMessage);
                logger(errorMessage);
                throw new Error(errorMessage);
              }
            },
            onmessage: (messageEvent) => {
              try {
                const data = parseMessage(messageEvent.data);
                if (data) {
                  latestRef.current.onMessage?.(data);
                }
              } catch (parseError) {
                const errorMessage = 'Failed to parse server data';
                setError(errorMessage);
                logger('Parse error:', parseError);
              }
            },
            onerror: (errorEvent) => {
              logger('Connection error:', errorEvent);
              setIsConnected(false);
              const errorMessage = 'Connection error';
              setError(errorMessage);

              // Attempt to reconnect after a delay
              reconnectTimeoutRef.current = setTimeout(() => {
                if (!abortController.signal.aborted) {
                  logger('Attempting to reconnect...');
                  setError(null);
                  void connect();
                }
              }, RECONNECT_DELAY);

              // Throw error to stop the current connection attempt
              throw errorEvent;
            },
            onclose: () => {
              logger('Connection closed by server');
              setIsConnected(false);
            },
          });
        } catch (connectionError) {
          logger('Failed to establish connection:', connectionError);
          if (!abortController.signal.aborted) {
            setIsConnected(false);
          }
        }
      };

      void connect();

      // Cleanup function
      return () => {
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
        logger('Stopped listening.');
        setIsConnected(false);
      };
    }, [latestRef, pathname, enabled]);

    // Manual cleanup method
    const disconnect = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
        setIsConnected(false);
      }
    };

    return {
      isConnected,
      error,
      disconnect,
    };
  };
};
